import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import EntitiesService from "../../services/EntitiesService";
import UsersService from "../../services/UsersService";
import StudentsGroupService from "../../services/StudentsGroupService";
import IEntityData from "../../types/Entities";
import IUsersData from "../../types/Users";
import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/event_form_banner.png";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useDefaultStyles } from "../../common/styles/default";
import SearchBar from "../../common/components/search/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

export default function FormStudentsGroupV2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const styles = useDefaultStyles();
  const { id } = useParams<{ id: string }>();

  const [idGroup, setIdGroup] = useState<number | undefined>(id ? parseInt(id, 10) : undefined);
  const [entities, setEntities] = useState<IEntityData[]>([]);
  const [name, setName] = useState("");
  const [studentsgroup, setStudentsGroup] = useState("");
  const [students, setStudents] = useState<IUsersData[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStudents, setFilteredStudents] = useState<IUsersData[]>([]);

  // Carregar entidades e grupo/alunos se edição
  useEffect(() => {
    setLoading(true);
    EntitiesService.getAll(1, 0)
      .then((response: any) => setEntities(response.data))
      .catch(() => toast(t("toast_msg_formestudentsgroup_entity"), { type: "error" }));
    if (idGroup) {
      StudentsGroupService.get(idGroup)
        .then((response: any) => {
          setIdGroup(response.data.id);
          setName(response.data.name);
          setStudentsGroup(response.data.entity_id);
          // Alunos já vinculados
          const arrStudents = response?.data?.students?.map((student: { id: number }) => student.id) || [];
          setSelectedStudents(arrStudents);
        })
        .catch(() => toast(t("toast_msg_formstudentsgroup_load"), { type: "error" }));
    }
    UsersService.getStudents()
      .then((response: any) => {
        setStudents(response.data);
        setFilteredStudents(response.data);
      })
      .catch(() => toast(t("toast_msg_formstudentsgroup_users"), { type: "error" }))
      .finally(() => setLoading(false));
  }, [idGroup, t]);

  const handleStudentsGroup = (event: SelectChangeEvent) => {
    setStudentsGroup(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    if (name === "" || studentsgroup === "") {
      toast(
        t(
          name === ""
            ? "toast_msg_formstudentsgroup_name"
            : "toast_msg_formestudentsgroup_entity"
        ),
        { type: "error" }
      );
      setLoading(false);
      return;
    }
    const dataStudentsGroup = {
      id: "",
      name: name,
      entity_id: studentsgroup,
      parent_id: "",
    };
    if (!idGroup) {
      StudentsGroupService.store(dataStudentsGroup)
        .then((response: any) => {
          setIdGroup(response.data.id);
          toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error, { type: "error" });
        })
        .finally(() => setLoading(false));
    } else {
      StudentsGroupService.update(idGroup, dataStudentsGroup)
        .then(() => {
          toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error, { type: "error" });
        })
        .finally(() => setLoading(false));
    }
  };

  const handleStudentSelect = (id: number) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleAddStudents = () => {
    if (selectedStudents.length === 0 || !idGroup) {
      toast(t("toast_msg_formstudentsgroup_users"), { type: "error" });
      return;
    }
    setLoading(true);
    const dataStudents = {
      id: idGroup,
      users: selectedStudents,
    };
    StudentsGroupService.linkStudents(dataStudents)
      .then(() => {
        toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        navigate("/studentsgroup/list");
      })
      .catch((e: any) => {
        toast(e?.response?.data?.Error, { type: "error" });
      })
      .finally(() => setLoading(false));
  };

  const normalize = (s: string) =>
    s
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, '')

  // Accepts payload from SearchBar: string | { q: string, filters?: Record }
  const handleSearch = (payload: any) => {
    try {
      let q = "";
      let filters: Record<string, any> | undefined = undefined;

      if (!payload) {
        setSearchQuery("");
        setFilteredStudents(students);
        return;
      }

      if (typeof payload === "string") {
        q = payload;
      } else if (typeof payload === "object") {
        q = payload.q ?? "";
        filters = payload.filters;
      }

      setSearchQuery(q);

      const normalized = normalize(String(q || ""));

      const result = students.filter((s) => {
        // apply filters if present
        if (filters) {
          for (const k of Object.keys(filters)) {
            const fv = filters[k];
            // simple equality check (coerce to string)
            if (fv !== undefined && fv !== null && String(s[k]) !== String(fv)) {
              return false;
            }
          }
        }

        if (!normalized) return true;

        // search fields
        const name = normalize(String(s.name || ""));
        const email = normalize(String(s.email || ""));
        const cpf = normalize(String(s.cpf || ""));
        const ra = s.ra ? normalize(String(s.ra)) : "";

        return (
          name.includes(normalized) ||
          email.includes(normalized) ||
          cpf.includes(normalized) ||
          ra.includes(normalized)
        );
      });

      setFilteredStudents(result);
    } catch (e) {
      // fallback: no filter
      setFilteredStudents(students);
    }
  };

  return (
    <Box>
      <Banner
        type="small"
        image={banner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <GroupsOutlinedIcon sx={{ width: "3em", height: "3em" }} />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {!id ? t("title_page_form_register_studentsgroup") : t("title_page_form_register_studentsgroup_edit")}
            </Typography>
          </Box>
        }
      />
      <Box className={styles.container}>
        {loading && (
          <Typography sx={{ color: 'primary.main', mb: 2 }}>{t('loading')}</Typography>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 2, width: "50%" }}>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              label={t("input_label_studentsgroup")}
              variant="outlined"
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setName(event.target.value);
              }}
            />
          </Box>
          <Box sx={{ mb: 2, width: "50%" }}>
            <FormControl fullWidth>
              <InputLabel id="studentsgroup-label">{t("input_label_entity")}</InputLabel>
              <Select
                labelId="studentsgroup-label"
                label={t("input_label_entity")}
                value={studentsgroup}
                onChange={handleStudentsGroup}
              >
                {entities.map((row, index) => (
                  <MenuItem key={index} value={row.id}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button type="submit" variant="contained" size="large">
            {!idGroup ? t("btn_create_class") : t("btn_salvar_class")}
          </Button>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            sx={{ ml: 2 }}
            onClick={() => {
              navigate("/studentsgroup/list");
            }}
          >
            {t("btn_return")}
          </Button>
        </Box>
        {/* Listagem de usuários com busca e seleção */}
        {idGroup !== undefined && (
          <Box sx={{ mt: 4, width: "100%" }}>
            <Typography variant="h5" sx={{ mb: 2 }}>{t("title_component_form_classes_students")}</Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {/* Coluna maior: listagem de usuários */}
              <Box sx={{ flex: 3 }}>
                <SearchBar onSearch={handleSearch} />
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table sx={{ minWidth: 600 }} aria-label="users table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_name")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_email")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_document1")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_document2")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_registration")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_profile")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_btn_select")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStudents.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell>{student.name}</TableCell>
                          <TableCell>{student.email}</TableCell>
                          <TableCell>{student.cpf}</TableCell>
                          <TableCell>{student.rg}</TableCell>
                          <TableCell>{student.ra}</TableCell>
                          <TableCell>{t(student.roles?.[0]?.name)}</TableCell>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => handleStudentSelect(student.id)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  sx={{ mt: 2 }}
                  onClick={handleAddStudents}
                >
                  {t("btn_add_students_class")}
                </Button>
              </Box>
              {/* Coluna menor: usuários selecionados */}
              <Box sx={{ flex: 1, bgcolor: '#f8fafc', borderRadius: 2, p: 2, minWidth: 260, maxWidth: 340, height: 'fit-content' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('Usuários selecionados')}</Typography>
                {students.filter(s => selectedStudents.includes(s.id)).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('Nenhum usuário selecionado')}</Typography>
                ) : (
                  students.filter(s => selectedStudents.includes(s.id)).map((student) => (
                    <Box key={student.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1, bgcolor: '#fff', borderRadius: 1, boxShadow: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>{student.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{student.email}</Typography>
                      </Box>
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={() => handleStudentSelect(student.id)}
                      >
                        {t('Remover')}
                      </Button>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
